import { FC, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import { TabsProvider } from "../../context/tabsContext";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useI18n } from "../../hooks/useTranslations";
import { useDueDatesApi } from "../../http/dueDates/api";
import { UpsertDueDate } from "../../http/dueDates/types";
import DueDateAttachments from "../dueDatesAttachments/DueDateAttachments";
import ModalDialog from "../ui/ModalDialog";
import Tabs, { TabContent, TabsContents } from "../ui/Tabs";
import DueDateFormNuovo, { DueDateFormBody } from "./DueDateForm";

type EditDueDateDialogProps = {
  dueDateId: string;
  onClose: (success?: boolean) => void;
  carId?: string;
};

const EditDueDate: FC<EditDueDateDialogProps> = ({ dueDateId, onClose, carId }: EditDueDateDialogProps) => {
  const location = useLocation();
  const navigate = useNavigate();

  const urlParameter = "due_date_tab";
  useEffect(() => {
    return () => {
      const searchParams = new URLSearchParams(location.search);
      searchParams.delete(urlParameter);

      navigate(
        {
          pathname: location.pathname,
          search: searchParams.toString() ? `?${searchParams.toString()}` : "",
        },
        { replace: true }, // replace current history entry
      );
    };
  }, []); // no deps here

  return (
    <TabsProvider urlParameterName={urlParameter}>
      <EditDueDateDialog dueDateId={dueDateId} onClose={onClose} carId={carId} />
    </TabsProvider>
  );
};

export default EditDueDate;

const EditDueDateDialog: FC<EditDueDateDialogProps> = ({ dueDateId, onClose, carId }: EditDueDateDialogProps) => {
  const i18n = useI18n();
  const aborter = useRef(new AbortController());
  const userSettings = useUserSettings();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const dueDatesApi = useDueDatesApi();

  const dueDateSwr = dueDatesApi.getAdminDueDate(dueDateId, aborter.current.signal);
  const [dueDate, isInitialized] = useDataInitializer(dueDateSwr.data, dueDateSwr.error);

  useEffect(() => {
    dueDateSwr.error && dispatch(showErrorNotification(i18n, dueDateSwr.error));
  }, [dueDateSwr.error]);

  useEffect(() => {
    dueDateId && dueDateSwr.trigger();
  }, [dueDateId]);

  const onSubmit = async (formData: DueDateFormBody): Promise<void> => {
    if (dueDate?.id) {
      const body: UpsertDueDate = {
        carId: formData.car.id,
        typeId: formData.type.id,
        date: formData.date,
        isSettled: formData.isSettled,
        currentVersionNumber: dueDate?.versionNumber,
        visibleToUserId: formData.visibleToUser ? formData.driver?.id : undefined,
      };

      setIsSubmitting(true);
      try {
        await dueDatesApi.updateAdminDueDate(dueDate.id, body);
        dispatch(showSuccessNotification(i18n.t));
        onClose(true);
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const tabComponents: TabContent[] = [
    {
      body: (
        <DueDateFormNuovo
          onSubmit={onSubmit}
          isDisabled={!userSettings.permissions.dueDates.update || isSubmitting}
          isLoading={!isInitialized}
          dueDate={dueDate}
          carId={carId}
        />
      ),
      eager: true,
    },
    {
      body: (
        <DueDateAttachments dueDateId={dueDateId} carId={carId! || dueDate?.car.id!} setIsDragging={setIsDragging} />
      ),
      withBorder: true,
    },
  ];

  const tabs = [{ title: i18n.t.common.generalData }, { title: i18n.t.common.attachments }];

  return (
    <ModalDialog onClose={onClose} title={i18n.t.dueDates.edit}>
      <Tabs tabs={tabs} isDialog />
      <TabsContents contents={tabComponents} noPaddingHorizontal isDragging={isDragging} />
    </ModalDialog>
  );
};
